import React from 'react'
import {
  Box,
  Divider as MuiDivider,
  styled,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { MultiPercentageBar } from './components/PercentageBar'
import Card from '../../components/Card'
import { colors } from '../../themes/colors'
import Legend from './components/Legend'
import { Gradients } from './gradients'
import { UnitText } from './components/UnitText'
import { SalesLineProps } from './storage'
import { TestIds } from '../../testIds'
import { formatNumber } from '../../util/helpers'

interface Props {
  data: SalesLineProps[]
}

const SalesLineContainer = styled('div')`
  display: flex;
  flex-direction: column;
  gap: 24px;
`

const SalesLineCard = styled(Card)``

const Wrapper = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: '16px',
  gap: '8px',
  [theme.breakpoints.up('lg')]: {
    flexDirection: 'row',
    gap: '56px',
  },
}))

const DetailView = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: '8px',
  [theme.breakpoints.up('lg')]: {
    minWidth: '240px',
    maxWidth: '240px',
    gap: '12px',
  },
}))

const SubTitleView = styled('div')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'row',
  gap: '24px',
  [theme.breakpoints.up('lg')]: {
    flexDirection: 'column',
    gap: '12px',
  },
  [theme.breakpoints.down('sm')]: {
    flexDirection: 'column',
    gap: '8px',
  },
}))

const Divider = styled(MuiDivider)(({ theme }) => ({
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
}))

const PercentageView = styled('div')`
  display: flex;
  flex-direction: column;
  width: 100%;
  gap: 8px;
`

const LegendView = styled('div')`
  display: flex;
  align-items: flex-end;
  justify-content: flex-start;
  gap: 14px;
`

const PrograssBarView = styled('div')`
  display: flex;
`

const Inline = styled('div')`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`

const unitMap = {
  tonne: 't',
  t: 't',
  each: 'unit',
  ea: 'unit',
  slg: 'sl',
  sling: 'sl',
  sl: 'sl',
  kg: 'kg',
  kilo: 'kg',
}

const unitMapLong = {
  tonne: 'tonne',
  t: 'tonne',
  each: 'unit',
  ea: 'unit',
  slg: 'sling',
  sling: 'sling',
  sl: 'sling',
  kg: 'kg',
  kilo: 'kg',
}

const createLegend = (line: SalesLineProps) => {
  return [
    {
      value: line.quantity.invoiced,
      label: `Invoiced: ${formatNumber(line.quantity.invoiced)}${formatUnitDisplay(line.unit)}`,
      iconColor: Gradients['primary'] as string,
    },
    {
      value: line.quantity.delivered,
      label: `Delivered: ${formatNumber(line.quantity.delivered)}${formatUnitDisplay(line.unit)}`,
      iconColor: colors.primary400,
    },
    {
      value: line.quantity.released,
      label: `Released: ${formatNumber(line.quantity.released)}${formatUnitDisplay(line.unit)}`,
      iconColor: colors.primary300,
    },
  ].filter((v) => v.value > 0)
}

const LegendBox = (salesLine: SalesLineProps) => {
  return (
    <Box
      sx={{ marginBottom: '5px' }}
      data-testid={TestIds.feat.agreement.salesLines.fulfillmentStatus}
    >
      <Legend values={createLegend(salesLine)} />
    </Box>
  )
}

export const formatUnitLong = (amount: number, unit?: string) => {
  if (amount > 1 && unit?.startsWith('ea')) {
    return `units`
  } else if (unit?.startsWith('ea')) {
    return `unit`
  }

  const _unit = unitMapLong[unit as keyof typeof unitMapLong] ?? unit

  return `${_unit}${amount > 1 ? 's' : ''}`
}

export const formatUnitDisplay = (unit?: string) => {
  if (unit?.startsWith('ea') || unit?.startsWith('un')) {
    return ``
  }

  const _unit = unitMap[unit as keyof typeof unitMap] ?? unit
  return _unit
}

export const SalesLine = ({ data }: Props) => {
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'))

  const createPercentages = (line: SalesLineProps) => {
    return [
      {
        percentage: (line.quantity.invoiced / line.quantity.committed) * 100,
        foreground: colors.primary500,
        total: 100,
        height: 30,
        gradient: 'primary',
        background: 'green',
      },

      {
        percentage: (line.quantity.delivered / line.quantity.committed) * 100,
        foreground: colors.primary400,
        total: 100,
        height: 30,
        background: 'green',
      },
      {
        percentage: (line.quantity.released / line.quantity.committed) * 100,
        foreground: colors.primary300,
        total: 100,
        height: 30,
        background: 'green',
      },
    ].filter((v) => v.percentage > 0)
  }

  const createTotalFulfilled = (saleLine: SalesLineProps) => {
    const avgI: number[] = []
    avgI.push(
      (saleLine.quantity.committed - saleLine.quantity.remaining) / saleLine.quantity.committed,
    )

    const i = Math.floor((avgI.reduce((pv, v) => pv + v, 0) / avgI.length) * 100)

    return i
  }

  return (
    <SalesLineContainer data-testid={TestIds.feat.agreement.salesLines.container}>
      {data.map((x, index) => (
        <SalesLineCard
          variant='outlined'
          key={`${x.productDescription}_${index}`}
          data-testid={TestIds.feat.agreement.salesLines.item}
          data-id={x.productVariantNumber}
          sx={{
            '&:hover': {
              boxShadow: 'none',
            },
          }}
        >
          <Wrapper>
            <DetailView>
              <Typography
                variant='h4'
                color={colors.grey800}
                data-testid={TestIds.feat.agreement.salesLines.productDescription}
              >
                {x.productDescription}
              </Typography>
              <SubTitleView>
                <Inline>
                  <Typography
                    variant='body2'
                    color={colors.grey700}
                    data-testid={TestIds.feat.agreement.salesLines.agreementTotalLabel}
                  >
                    Agreement total:
                  </Typography>
                  <Typography
                    variant='body2'
                    sx={{ fontWeight: 700 }}
                    data-testid={TestIds.feat.agreement.salesLines.agreementTotal}
                  >
                    {x.quantity.committed} {formatUnitLong(x.quantity.committed, x.unit)}
                  </Typography>
                </Inline>
                <Inline>
                  <Typography
                    variant='body2'
                    color={colors.grey700}
                    data-testid={TestIds.feat.agreement.salesLines.boughtPriceLabel}
                  >
                    Price:
                  </Typography>
                  <Typography
                    variant='body2'
                    color={colors.secondary700}
                    sx={{ fontWeight: 700 }}
                    data-testid={TestIds.feat.agreement.salesLines.boughtPrice}
                  >
                    ${x.unitPrice} per {formatUnitLong(0, x.unit)}
                  </Typography>
                  <Typography
                    component={'span'}
                    sx={{ fontSize: '12px', lineHeight: '18px', color: colors.secondary700 }}
                  >
                    GST excl.
                  </Typography>
                </Inline>
              </SubTitleView>
            </DetailView>
            <Divider color={colors.primary100} />
            <PercentageView>
              <LegendView>
                <UnitText
                  label='Fulfilled'
                  unit={`${formatNumber(
                    x.quantity.invoiced + x.quantity.delivered + x.quantity.released,
                  )}${formatUnitDisplay(x.unit)}`}
                  testId={TestIds.feat.agreement.salesLines.fulfilled}
                />
                {!isSmall && LegendBox(x)}
                <Box sx={{ ml: 'auto' }}>
                  <UnitText
                    sx={{ textAlign: 'end' }}
                    label='Remaining'
                    unit={`${formatNumber(x.quantity.remaining)}${formatUnitDisplay(x.unit)}`}
                    color='secondary'
                    testId={TestIds.feat.agreement.salesLines.remaining}
                  />
                </Box>
              </LegendView>
              {isSmall && LegendBox(x)}
              <PrograssBarView>
                <MultiPercentageBar
                  values={createPercentages(x)}
                  totalFulfilled={createTotalFulfilled(x)}
                  background={colors.primary100}
                  height={17}
                  data-testid={TestIds.feat.agreement.salesLines.fulfillmentBar}
                />
              </PrograssBarView>
            </PercentageView>
          </Wrapper>
        </SalesLineCard>
      ))}
    </SalesLineContainer>
  )
}

export default SalesLine
